/**
 * 과목 관리 페이지
 * 설정 → 학습 관리 → 과목 관리
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config/api';
import './SubjectManagementPage.css';

interface Subject {
  id: string;
  organization_id: string;
  name: string;
  subject_code: string;
  is_default: boolean;
  display_order: number;
  is_active: boolean;
}

const SubjectManagementPage: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);

  // 과목 목록 조회
  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const token =
        localStorage.getItem('auth_access_token') ||
        localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/subjects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubjects(data.sort((a: Subject, b: Subject) => a.display_order - b.display_order));
      }
    } catch (error) {
      console.error('과목 목록 조회 실패:', error);
      alert('과목 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  // 새 과목 추가
  const handleAddSubject = async () => {
    if (!newSubjectName.trim()) {
      alert('과목명을 입력하세요.');
      return;
    }

    try {
      const token =
        localStorage.getItem('auth_access_token') ||
        localStorage.getItem('accessToken');

      // 마지막 display_order 찾기
      const maxOrder = subjects.length > 0
        ? Math.max(...subjects.map(s => s.display_order))
        : 0;

      const response = await fetch(`${API_BASE_URL}/subjects`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newSubjectName,
          display_order: maxOrder + 1,
        }),
      });

      if (response.ok) {
        alert('과목이 추가되었습니다!');
        setNewSubjectName('');
        await fetchSubjects();
      } else {
        const error = await response.json();
        alert(`과목 추가 실패: ${error.detail || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('과목 추가 실패:', error);
      alert('과목 추가 중 오류가 발생했습니다.');
    }
  };

  // 과목 삭제
  const handleDeleteSubject = async (subjectId: string, subjectName: string, isDefault: boolean) => {
    if (isDefault) {
      alert('기본 과목은 삭제할 수 없습니다.');
      return;
    }

    if (!window.confirm(`"${subjectName}" 과목을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token =
        localStorage.getItem('auth_access_token') ||
        localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/subjects/${subjectId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        alert('과목이 삭제되었습니다!');
        await fetchSubjects();
      } else {
        const error = await response.json();
        alert(`과목 삭제 실패: ${error.detail || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('과목 삭제 실패:', error);
      alert('과목 삭제 중 오류가 발생했습니다.');
    }
  };

  // 순서 변경
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newSubjects = [...subjects];
    [newSubjects[index], newSubjects[index - 1]] = [newSubjects[index - 1], newSubjects[index]];
    setSubjects(newSubjects);
  };

  const handleMoveDown = (index: number) => {
    if (index === subjects.length - 1) return;
    const newSubjects = [...subjects];
    [newSubjects[index], newSubjects[index + 1]] = [newSubjects[index + 1], newSubjects[index]];
    setSubjects(newSubjects);
  };

  // 순서 저장 (TODO: API 구현 필요)
  const handleSaveOrder = async () => {
    alert('순서 저장 기능은 추후 구현 예정입니다.');
    // TODO: PATCH /subjects/reorder API 구현
  };

  return (
    <div className="subject-management-page">
      <div className="subject-header">
        <h1>📚 과목 관리</h1>
        <p>독서실에서 사용할 과목을 관리합니다.</p>
      </div>

      {/* 새 과목 추가 */}
      <div className="add-subject-section">
        <h2>✨ 새 과목 추가</h2>
        <div className="add-subject-form">
          <input
            type="text"
            placeholder="과목명 입력 (예: 중국어, 경제)"
            value={newSubjectName}
            onChange={(e) => setNewSubjectName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddSubject()}
          />
          <button onClick={handleAddSubject} className="btn-add">
            추가
          </button>
        </div>
      </div>

      {/* 과목 목록 */}
      <div className="subjects-list-section">
        <h2>📋 과목 목록</h2>

        {loading ? (
          <p>로딩 중...</p>
        ) : subjects.length === 0 ? (
          <p>등록된 과목이 없습니다.</p>
        ) : (
          <>
            <table className="subjects-table">
              <thead>
                <tr>
                  <th>순서</th>
                  <th>과목 코드</th>
                  <th>과목명</th>
                  <th>유형</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((subject, index) => (
                  <tr key={subject.id}>
                    <td>
                      <div className="order-controls">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="btn-order"
                        >
                          ▲
                        </button>
                        <span>{index + 1}</span>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === subjects.length - 1}
                          className="btn-order"
                        >
                          ▼
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="subject-code">{subject.subject_code}</span>
                    </td>
                    <td>
                      <strong>{subject.name}</strong>
                    </td>
                    <td>
                      {subject.is_default ? (
                        <span className="badge-default">기본</span>
                      ) : (
                        <span className="badge-custom">커스텀</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() =>
                          handleDeleteSubject(subject.id, subject.name, subject.is_default)
                        }
                        className="btn-delete"
                        disabled={subject.is_default}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="save-order-section">
              <button onClick={handleSaveOrder} className="btn-save-order">
                순서 저장
              </button>
            </div>
          </>
        )}
      </div>

      {/* 안내 메시지 */}
      <div className="info-section">
        <h3>ℹ️ 안내</h3>
        <ul>
          <li>
            <strong>기본 과목</strong>: 국어, 수학, 영어, 한국사, 사회, 과학, 제2외국어는 삭제할 수
            없습니다.
          </li>
          <li>
            <strong>커스텀 과목</strong>: 독서실에 필요한 과목을 자유롭게 추가할 수 있습니다.
          </li>
          <li>
            <strong>과목 코드</strong>: 자동으로 생성되며, 데이터 관리 및 내보내기 시 사용됩니다.
          </li>
          <li>
            <strong>순서 변경</strong>: ▲▼ 버튼으로 순서를 변경하고 '순서 저장' 버튼을 눌러
            저장하세요.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SubjectManagementPage;
